import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  Signal,
  type OnDestroy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  TrackByFunctions,
  TypedFormControl,
  validFormControlChanges$,
} from '@principle-theorem/ng-shared';
import { ReportingPermissions } from '@principle-theorem/principle-core/features';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PractitionerIncomeReportFacade } from '../store/practitioner-income.facade';
import { PractitionerIncomeReportType } from '../store/practitioner-income.reducers';

interface IReportType {
  label: string;
  value: PractitionerIncomeReportType;
}

@Component({
  selector: 'pr-practitioner-income-report-type-selector',
  templateUrl: './practitioner-income-report-type-selector.component.html',
  styleUrls: ['./practitioner-income-report-type-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PractitionerIncomeReportTypeSelectorComponent
  implements OnDestroy
{
  private _onDestroy$ = new Subject<void>();
  trackByReportType = TrackByFunctions.field<IReportType>('value');
  reportTypes: Signal<IReportType[]>;
  reportTypeControl = new TypedFormControl<PractitionerIncomeReportType>(
    PractitionerIncomeReportType.ByTransactionDate
  );

  constructor(
    private _reportFacade: PractitionerIncomeReportFacade,
    organisation: OrganisationService
  ) {
    this._reportFacade.reportType$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((reportType) => this.reportTypeControl.setValue(reportType));
    validFormControlChanges$(this.reportTypeControl)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((reportType) => this._reportFacade.setReportType(reportType));

    const hasSinglePractitionerPermissions = toSignal(
      organisation.userHasPermission$(
        ReportingPermissions.ReportingReportOnlySelfInPractitioners
      )
    );

    const hasAllPractitionerPermissions = toSignal(
      organisation.userHasPermission$(
        ReportingPermissions.ReportingReportPractitioners
      )
    );

    effect(() => {
      if (
        hasSinglePractitionerPermissions() &&
        !hasAllPractitionerPermissions()
      ) {
        this.reportTypeControl.disable();
      } else {
        this.reportTypeControl.enable();
      }
    });

    this.reportTypes = computed(() => {
      if (
        !hasSinglePractitionerPermissions() &&
        !hasAllPractitionerPermissions()
      ) {
        return [];
      }

      if (
        hasSinglePractitionerPermissions() &&
        !hasAllPractitionerPermissions()
      ) {
        return [
          {
            label: 'By Transaction Date',
            value: PractitionerIncomeReportType.ByTransactionDate,
          },
        ];
      }

      return [
        {
          label: 'By Invoice Issued Date',
          value: PractitionerIncomeReportType.ByInvoiceIssuedDate,
        },
        {
          label: 'By Invoice Paid Date',
          value: PractitionerIncomeReportType.ByInvoicePaidDate,
        },
        {
          label: 'By Transaction Date',
          value: PractitionerIncomeReportType.ByTransactionDate,
        },
      ];
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
